/** Opt-in #836 flight recorder. No order/feed subscriptions or payloads. */
import { validBrowserSample, type BrowserSample } from '../../../relay/src/diagnostics/browserSample';
export type { BrowserSample } from '../../../relay/src/diagnostics/browserSample';
export const DIAGNOSTIC_WINDOW_MS = 30 * 60_000;
export const DIAGNOSTIC_INTERVAL_MS = 30_000;
export const DIAGNOSTIC_RING_SIZE = 60;
const KEY = 'g1.browser-diagnostics.v1';

type RecorderState = { active: boolean; automatic: boolean; recovered: boolean; samples: number; uploaded: number; failed: number; message: string };
type Transport = (sample: BrowserSample, signal: AbortSignal) => Promise<boolean>;
const idle: RecorderState = { active: false, automatic: false, recovered: false, samples: 0, uploaded: 0, failed: 0, message: 'Off' };
let state = idle;
let ring: BrowserSample[] = [];
let owner: string | null = null;
let timer: ReturnType<typeof setInterval> | undefined;
let request: AbortController | undefined;
let generation = 0;
const listeners = new Set<() => void>();

export const diagnosticState = () => state;
export function subscribeDiagnostics(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
function update(patch: Partial<RecorderState>) {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
}

export function diagnosticRoute(path: string): BrowserSample['route'] {
  if (path.startsWith('/game/')) return 'game';
  if (path === '/positions' || path.startsWith('/positions/') || path === '/trading/positions') return 'positions';
  if (path === '/settings' || path.startsWith('/settings/') || path === '/account/settings') return 'settings';
  return 'other';
}

function finite(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.min(Math.round(value), Number.MAX_SAFE_INTEGER) : null;
}

export function collectBrowserSample(session: string, sequence: number, started: number): BrowserSample {
  // Walk at most 10k elements / 3 ms. Do not retain a NodeList or inspect text,
  // force layout, traverse stores, capture stacks, or request a heap snapshot.
  const scanStart = performance.now();
  const walker = document.createTreeWalker(document.documentElement, NodeFilter.SHOW_ELEMENT);
  let elements = 0;
  let canvases = 0;
  let canvasPixels = 0;
  let domTruncated = false;
  while (walker.nextNode()) {
    if (++elements >= 10_000 || (elements % 64 === 0 && performance.now() - scanStart >= 3)) {
      domTruncated = true;
      break;
    }
    if (walker.currentNode instanceof HTMLCanvasElement) {
      canvases++;
      canvasPixels += walker.currentNode.width * walker.currentNode.height;
    }
  }
  const memory = (performance as Performance & {
    memory?: { usedJSHeapSize?: number; totalJSHeapSize?: number; jsHeapSizeLimit?: number };
  }).memory;
  return {
    version: 1, session, sequence,
    build: typeof __BUILD_COMMIT__ === 'string' && /^[a-f0-9]{7,40}$/.test(__BUILD_COMMIT__)
      ? __BUILD_COMMIT__ : 'unknown',
    wallMs: Date.now(), elapsedMs: Math.round(performance.now() - started),
    pageAgeMs: Math.round(performance.now()), route: diagnosticRoute(location.pathname),
    hidden: document.hidden,
    heapUsed: finite(memory?.usedJSHeapSize), heapTotal: finite(memory?.totalJSHeapSize),
    heapLimit: finite(memory?.jsHeapSizeLimit), elements, canvases,
    canvasPixels: finite(canvasPixels) ?? 0, domTruncated,
  };
}

/** Authentication changes stop capture and prevent another user exporting it. */
export function setDiagnosticOwner(next: string | null) {
  if (owner === next) return;
  stopBrowserDiagnostics();
  const previous = owner;
  owner = next;
  ring = [];
  state = idle;
  try {
    const raw = sessionStorage.getItem(KEY);
    const saved = raw && raw.length < 64_000 ? JSON.parse(raw) : null;
    if (!previous && next && saved?.owner === next && Date.now() - saved.savedAt >= 0
      && Date.now() - saved.savedAt < 24 * 60 * 60_000 && Array.isArray(saved.samples)
      && saved.samples.length <= DIAGNOSTIC_RING_SIZE && saved.samples.every(validBrowserSample)) {
      ring = saved.samples;
      state = { ...idle, recovered: true, samples: ring.length, message: 'Previous checkpoints recovered. Capture is off.' };
    } else sessionStorage.removeItem(KEY);
  } catch { /* Invalid/disabled storage is not used or exported. */ }
  update({});
}

export function stopBrowserDiagnostics() {
  generation++;
  clearInterval(timer);
  timer = undefined;
  request?.abort();
  request = undefined;
  if (state.active) update({ active: false, message: state.automatic
    ? 'Automatic capture paused for this visit. It resumes after refresh while support has it enabled.'
    : 'Stopped. Captured checkpoints remain available.' });
}

export function startBrowserDiagnostics(send: Transport, durationMs = DIAGNOSTIC_WINDOW_MS, automatic = false) {
  if (ring.length && !automatic) return; // Manual capture never overwrites recovered evidence.
  stopBrowserDiagnostics();
  if (!owner) return;
  const run = generation;
  let session = crypto.randomUUID();
  const captureStarted = performance.now();
  let started = captureStarted;
  if (!automatic) ring = [];
  state = { ...idle, active: true, automatic, samples: ring.length, message: automatic
    ? 'Support-enabled automatic capture. Latest 60 checkpoints retained in this tab.'
    : 'Recording for up to 30 minutes in this tab.' };
  let sequence = 0;
  const tick = () => {
    if (run !== generation) return;
    const now = performance.now();
    if (now - captureStarted >= (automatic ? durationMs : Math.min(durationMs, DIAGNOSTIC_WINDOW_MS))) {
      stopBrowserDiagnostics();
      return;
    }
    try {
      // Keep the existing bounded wire schema. Automatic collection rotates
      // segments, not timers or queues; the local ring remains capped at 60.
      if (automatic && (sequence >= DIAGNOSTIC_RING_SIZE || now - started >= DIAGNOSTIC_WINDOW_MS)) {
        session = crypto.randomUUID(); sequence = 0; started = now;
      }
      const sample = collectBrowserSample(session, ++sequence, started);
      ring.push(sample);
      if (ring.length > DIAGNOSTIC_RING_SIZE) ring.shift();
      try { sessionStorage.setItem(KEY, JSON.stringify({ owner, savedAt: Date.now(), samples: ring })); }
      catch { /* A full/disabled store must never affect trading. */ }
      update({ samples: ring.length });
      if (request) return; // No backlog, resend loop, or parallel upload.
      const controller = new AbortController();
      request = controller;
      const timeout = setTimeout(() => controller.abort(), 5_000);
      void Promise.resolve().then(() => send(sample, controller.signal)).then((ok) => {
        if (run !== generation) return;
        update(ok ? { uploaded: state.uploaded + 1 } : { failed: state.failed + 1 });
      }).catch(() => {
        if (run === generation) update({ failed: state.failed + 1 });
      }).finally(() => {
        clearTimeout(timeout);
        if (request === controller) request = undefined;
      });
    } catch {
      stopBrowserDiagnostics();
      update({ message: 'Capture stopped because sampling was unavailable.' });
    }
  };
  timer = setInterval(tick, DIAGNOSTIC_INTERVAL_MS);
  tick();
}

export function clearBrowserDiagnostics() {
  stopBrowserDiagnostics();
  ring = [];
  state = idle;
  try { sessionStorage.removeItem(KEY); } catch { /* optional storage */ }
  update({});
}

/** Generated or owner-matched, schema-validated recovered scalar samples. */
export function exportBrowserDiagnostics(): string {
  return JSON.stringify({ version: 1, samples: ring }, null, 2);
}
